import { useMemo, Activity, useState, useEffect, useCallback } from 'react';

import {
  IconClock,
  IconCalendarEvent,
  IconDeviceGamepad2,
} from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import EmptyState from '@/components/EmptyState';
import Select from '@/components/Select';
import ParentDashboardSkeleton from '@/components/SkeletonLoader/ParentDashboardSkeleton';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import TagCapsule from '@/components/TagCapsule';
import { LessonStageConfig } from '@/constants/constant';
import { ELessonStageType } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getParentChildGames,
  getParentChildSummary,
  getParentChildWeeklyActivity,
  getParentChildLearningActivityDistribution,
} from '@/service/dashboardService';
import { useParentStore } from '@/store/useParentStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import {
  IParentGame,
  IParentChildSummary,
  IParentWeeklyActivityItem,
  IParentLearningActivityDistributionItem,
} from '@/types/parent';
import { formatLastActive, formatSecondsToDuration } from '@/utils';

import GameDetailsDrawer from './components/GameDetailsDrawer';
import GameDistributionChart from './components/GameDistributionChart';
import WeeklyActivityChart from './components/WeeklyActivityChart';

// ─── Constants ────────────────────────────────────────────────────────────────

const WEEKLY_ACTIVITY_FILTER_MAP: Record<string, number> = {
  'this-week': 1,
  'last-week': 2,
  'this-month': 3,
  'last-month': 4,
};

const GAME_DISTRIBUTION_COLORS = [
  '#9333EA',
  '#22C55E',
  '#EC4899',
  '#F97316',
  '#3B82F6',
  '#EAB308',
  '#14B8A6',
  '#EF4444',
];

// ─── Component ────────────────────────────────────────────────────────────────

const ParentDashboard = () => {
  const { formatText } = useFormatText();
  const setTitle = usePageTitle((state) => state.setTitle);
  const { isTableLoading, isScreenLoading, setScreenLoading, setTableLoading } =
    useSkeletonStore();

  const { selectedChild, children } = useParentStore();
  const selectedChildId = selectedChild?.id;

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { settings } = useSettingsStore();
  const language = settings?.language;
  const [games, setGames] = useState<IParentGame[]>([]);
  const [totalGames, setTotalGames] = useState(0);
  const [summary, setSummary] = useState<IParentChildSummary | null>(null);
  const [selectedGame, setSelectedGame] = useState<IParentGame | null>(null);
  const [weeklyActivityRange, setWeeklyActivityRange] =
    useState<string>('this-week');
  const [weeklyActivity, setWeeklyActivity] = useState<
    IParentWeeklyActivityItem[]
  >([]);
  const [gameDistribution, setGameDistribution] = useState<
    IParentLearningActivityDistributionItem[]
  >([]);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | ''>('');

  const weeklyActivityOptions = useMemo(
    () => [
      {
        value: 'this-week',
        label: formatText('parent-dashboard.weekly-activity.this-week'),
      },
      {
        value: 'last-week',
        label: formatText('parent-dashboard.weekly-activity.last-week'),
      },
      {
        value: 'this-month',
        label: formatText('parent-dashboard.weekly-activity.this-month'),
      },
      {
        value: 'last-month',
        label: formatText('parent-dashboard.weekly-activity.last-month'),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings]
  );

  useEffect(() => {
    setTitle(formatText('parent-dashboard.title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const isArabic = settings?.language === 'ar';

  const gameDistributionChartData = useMemo(
    () =>
      gameDistribution?.map((item, index) => ({
        label: isArabic ? item?.game_goal_ar : item?.game_goal_en,
        value: item?.percentage,
        color:
          GAME_DISTRIBUTION_COLORS[index % GAME_DISTRIBUTION_COLORS?.length],
      })),
    [gameDistribution, isArabic]
  );

  const fetchSummary = useCallback(async () => {
    if (!selectedChildId) return;

    try {
      const response = await getParentChildSummary(selectedChildId);
      if (response?.status) {
        setSummary(response?.data ?? null);
      }
    } catch (error) {
      console.error(error);
    }
  }, [selectedChildId]);

  const fetchGameDistribution = useCallback(async () => {
    if (!selectedChildId) return;

    try {
      const response =
        await getParentChildLearningActivityDistribution(selectedChildId);
      if (response?.status) {
        setGameDistribution(response?.data?.distribution ?? []);
      }
    } catch (error) {
      console.error(error);
    }
  }, [selectedChildId]);

  const fetchWeeklyActivity = useCallback(async () => {
    if (!selectedChildId) return;

    const filter = WEEKLY_ACTIVITY_FILTER_MAP[weeklyActivityRange] ?? 1;

    try {
      const response = await getParentChildWeeklyActivity(
        selectedChildId,
        filter
      );
      if (response?.status) {
        setWeeklyActivity(response?.data?.activity ?? []);
      }
    } catch (error) {
      console.error(error);
    }
  }, [selectedChildId, weeklyActivityRange]);

  const fetchGames = useCallback(async () => {
    if (!selectedChildId) return;

    setTableLoading(true);
    try {
      const query: Record<string, unknown> = {
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
      };

      if (sortField && sortDirection) {
        query[`order[${sortField}]`] = sortDirection;
      }

      const response = await getParentChildGames(selectedChildId, query);
      if (response?.status) {
        setGames(response?.data ?? []);
        setTotalGames(response?.total ?? 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTableLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChildId, currentPage, pageSize, sortField, sortDirection]);

  // Load data when child changes
  useEffect(() => {
    if (!selectedChildId) return;

    const loadData = async () => {
      setScreenLoading(true);
      setCurrentPage(1);
      try {
        await Promise.all([
          fetchSummary(),
          fetchGames(),
          fetchWeeklyActivity(),
          fetchGameDistribution(),
        ]);
      } finally {
        setScreenLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChildId]);

  // Refetch weekly activity when filter changes
  useEffect(() => {
    if (!isScreenLoading && selectedChildId) {
      fetchWeeklyActivity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyActivityRange, language]);

  // Refetch games on page change
  useEffect(() => {
    if (!isScreenLoading && selectedChildId) {
      fetchGames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortField, sortDirection]);

  // ── Sort Handler ────────────────────────────────────────────────────────────

  const handleSort = (key: string) => {
    setCurrentPage(1);

    if (sortField === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortField('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortField(key);
      setSortDirection('ASC');
    }
  };

  // ── Table Columns ───────────────────────────────────────────────────────────

  const gameColumns = useMemo(
    () => [
      {
        key: 'game_name',
        title: 'parent-dashboard.current-game.game-name',
        sorter: true,
        handleSort: () => handleSort('game_name'),
        render: (record: IParentGame) => {
          const gameName =
            language === 'ar' ? record?.title_ar : record?.title_en;
          return (
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-light-100">
                <UserAvatarList
                  user={gameName}
                  isSquare={true}
                  image={record.game_image}
                  className="h-10 w-10 text-neutral-primary"
                />
              </div>
              <span className="text-sm font-medium text-neutral-primary">
                {gameName}
              </span>
            </div>
          );
        },
      },
      {
        key: 'quarter',
        title: 'parent-dashboard.current-game.quarter',
        sorter: true,
        handleSort: () => handleSort('quarter'),
        render: (record: IParentGame) => (
          <span className="text-sm text-neutral-secondary">
            {record.quarters ?? '-'}
          </span>
        ),
      },
      {
        key: 'round',
        title: 'parent-dashboard.current-game.round',
        sorter: true,
        handleSort: () => handleSort('round'),
        render: (record: IParentGame) => (
          <span className="text-sm text-neutral-secondary">
            {record.rounds ?? '-'}
          </span>
        ),
      },
      {
        key: 'skillFocus',
        title: 'parent-dashboard.current-game.skill-focus',
        sorter: false,
        // handleSort: () => handleSort('skills'),
        render: (record: IParentGame) => {
          const titles =
            record.skills?.map((skill) =>
              language === 'ar' ? skill?.skill_title_ar : skill?.skill_title_en
            ) ?? [];
          const text = titles.length ? titles.join(', ') : '-';
          return (
            <span
              className="text-sm text-neutral-secondary block truncate max-w-60"
              title={text}
            >
              {text}
            </span>
          );
        },
      },
      {
        key: 'stage',
        title: 'parent-dashboard.current-game.level',
        sorter: true,
        handleSort: () => handleSort('stage'),
        render: (record: IParentGame) => {
          const config = record?.stage
            ? LessonStageConfig[record.stage as ELessonStageType]
            : null;

          return (
            <TagCapsule
              value={config?.label ? formatText(config.label) : ''}
              className={config?.className}
            />
          );
        },
      },
      // {
      //   key: 'status',
      //   title: 'parent-dashboard.current-game.status',
      //   sorter: true,
      //   handleSort: () => handleSort('status'),
      //   render: (record: IParentGame) => {
      //     const config = record?.status
      //       ? LessonStatusConfig[record?.status as ELessonStatus]
      //       : null;
      //     return (
      //       <TagCapsule
      //         value={config?.label ? formatText(config.label) : ''}
      //         className={config?.className}
      //       />
      //     );
      //   },
      // },
      {
        key: 'last_updated',
        title: 'parent-dashboard.current-game.last-update',
        sorter: true,
        handleSort: () => handleSort('last_updated'),
        render: (record: IParentGame) => (
          <span className="text-sm text-neutral-secondary">
            {formatLastActive(record?.last_updated, formatText)}
          </span>
        ),
      },
      {
        key: 'action',
        title: 'parent-dashboard.current-game.action',
        render: (record: IParentGame) => (
          <Button
            variant="link"
            className="text-brand-default!"
            onClick={() => setSelectedGame(record)}
            label={formatText('parent-dashboard.current-game.view')}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatText, sortField, sortDirection, language]
  );

  if (isScreenLoading) {
    return <ParentDashboardSkeleton />;
  }

  const handleWeeklyActivityRangeChange = (option: string | string[]) => {
    if (typeof option === 'string') {
      setWeeklyActivityRange(option);
    }
  };

  // No children at all → Dashboard Empty State
  if (children.length === 0) {
    return (
      <EmptyState
        imageSrc="/not-found.svg"
        imageAlt="No children found"
        title={formatText('parent-dashboard.empty-state.no-children-title')}
        description={formatText(
          'parent-dashboard.empty-state.no-children-description'
        )}
        dataTestId="parent-dashboard-empty-state"
      />
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <SummaryCard
          variant="dashboard"
          label={formatText('parent-dashboard.metrics.active-days')}
          value={summary?.active_days ?? 0}
          icon={
            <IconCalendarEvent size={24} className="text-primary-default" />
          }
        />
        <SummaryCard
          variant="dashboard"
          label={formatText('parent-dashboard.metrics.learning-hours')}
          value={formatSecondsToDuration(summary?.learning_hours ?? 0)}
          icon={<IconClock size={24} className="text-success-default" />}
        />
        <SummaryCard
          variant="dashboard"
          label={formatText('parent-dashboard.metrics.games-played')}
          value={summary?.games_played ?? 0}
          icon={
            <IconDeviceGamepad2 size={24} className="text-warning-default" />
          }
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
        {/* Weekly Activity */}
        <div className="rounded-xl border border-neutral-light-200 bg-card p-4 shadow-2xs">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold text-neutral-primary">
              {formatText('parent-dashboard.weekly-activity.title')}
            </h2>
            <Select
              options={weeklyActivityOptions}
              selected={weeklyActivityRange}
              onChange={handleWeeklyActivityRangeChange}
              className="w-36! py-1.5"
              rootClassName="w-fit"
            />
          </div>
          <WeeklyActivityChart data={weeklyActivity} />
        </div>

        {/* Game Type Distribution */}
        <div className="rounded-xl border border-neutral-light-200 bg-card p-4 shadow-2xs">
          <h2 className="mb-4 text-base font-semibold text-neutral-primary">
            {formatText('parent-dashboard.game-distribution.title')}
          </h2>
          {gameDistributionChartData?.length > 0 ? (
            <GameDistributionChart data={gameDistributionChartData} />
          ) : (
            <EmptyState
              imageSrc="/not-found.svg"
              imageAlt="no-data"
              title={formatText(
                'parent-dashboard.game-distribution.empty-title'
              )}
              description={formatText(
                'parent-dashboard.game-distribution.empty-description'
              )}
              width={120}
              height={120}
              className="h-auto! min-h-50! gap-3! py-4!"
              titleClassName="text-base!"
              dataTestId="parent-dashboard-game-distribution-empty"
            />
          )}
        </div>
      </div>

      {/* Current Game Table */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-neutral-primary">
          {formatText('parent-dashboard.current-game.title')}
        </h2>
        <CustomTable
          dataSource={games}
          columns={gameColumns}
          rowKey="id"
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={totalGames}
          pageSize={pageSize}
          isLoading={isTableLoading}
          fieldName={sortField}
          sortDirection={sortDirection}
          isEmpty={games.length === 0 && !isTableLoading}
          emptyClassName="h-auto! min-h-100!"
          onRowClick={(id: string) => {
            const game = games?.find((g) => g.id === id);
            if (game) setSelectedGame(game);
          }}
        />
      </div>
      <Activity mode={selectedGame !== null ? 'visible' : 'hidden'}>
        <GameDetailsDrawer
          game={selectedGame}
          onClose={() => setSelectedGame(null)}
        />
      </Activity>
    </div>
  );
};

export default ParentDashboard;
